import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TDailyReport } from "@/schemas/daily-report.schema";
import { TWasteType } from "@/schemas/waste-type.schema";
import {
  TWeightScaleBreakdown,
  weightScaleBreakdown,
} from "@/schemas/weigh-scale-ticket.schema";

type AddBreakdownSheetProps = {
  expanded: boolean;
  toggleOpen: () => void;
  selectedBreakdownId: number;
  wastetypeData: TWasteType[];
  defaultValues?: TResourceRecoveryDetails["breakDown"];
};

const AddBreakdownSheet = ({
  expanded,
  toggleOpen,
  selectedBreakdownId,
  wastetypeData,
  defaultValues,
}: AddBreakdownSheetProps): React.JSX.Element => {
  const { setValue, watch } = useFormContext<TDailyReport>();

  const form = useForm<TWeightScaleBreakdown>({
    resolver: zodResolver(weightScaleBreakdown),
    values: {
      mixedWaste:
        defaultValues?.mixedWaste?.map((d) => ({
          wasteTypeId: d.wasteTypeId || "",
          percentage: d.percentage || 0,
          wasteName: d.wasteName || "",
        })) || [],
      otherWaste:
        defaultValues?.otherWaste?.map((d) => ({
          wasteTypeId: d.wasteTypeId || "",
          wasteName: d.wasteName || "",
        })) || [],
    },
  });

  const breakDownData = watch(
    `resourceRecoveryDetails.manualData.${selectedBreakdownId}.breakDown`,
  );

  const {
    fields: mixedWasteFields,
    append: appendWasteFields,
    remove: removeWasteFields,
  } = useFieldArray({
    name: `mixedWaste`,
    control: form.control,
    keyName: "mixedId",
  });
  const {
    fields: otherWasteFields,
    append: appendOtherWasteFields,
    remove: removeOtherWasteFields,
  } = useFieldArray({
    name: `otherWaste`,
    control: form.control,
    keyName: "otherId",
  });

  const handleSaveBreakdown = (data: TWeightScaleBreakdown): void => {
    setValue(
      `resourceRecoveryDetails.manualData.${selectedBreakdownId}.breakDown`,
      data,
    );
    toggleOpen();
  };

  useEffect(() => {
    if (breakDownData) {
      form.reset({
        mixedWaste:
          breakDownData?.mixedWaste?.map((d) => ({
            wasteTypeId: d.wasteTypeId || "",
            percentage: d.percentage || 0,
            wasteName: d.wasteName || "",
          })) || [],
        otherWaste:
          breakDownData?.otherWaste?.map((d) => ({
            wasteTypeId: d.wasteTypeId || "",
            wasteName: d.wasteName || "",
          })) || [],
      });
    }
  }, [breakDownData]);

  return (
    <Form {...form}>
      <Sheet open={expanded} onOpenChange={toggleOpen}>
        <SheetContent className='pt-12 sm:max-w-[400px] lg:max-w-[480px] '>
          <div className=''>
            <SheetHeader className='mb-4'>
              <h3 className='mt-1 text-lg font-normal'>Breakdown</h3>
              <p className='text-sm text-aldgray'>
                Please select waste type that this mixed waste contain
              </p>
            </SheetHeader>
            {/* Waste Type */}
            <ScrollArea className='max-h-[80vh] overflow-y-scroll scrollbar-none'>
              <div className='space-y-2'>
                <Table>
                  <TableHeader className=''>
                    <TableRow className='bg-secondary hover:bg-secondary'>
                      <TableHead>SN</TableHead>
                      <TableHead
                        style={{
                          width: "210px",
                          padding: 0,
                        }}
                      >
                        WASTE TYPE
                      </TableHead>
                      <TableHead className='text-center'>%</TableHead>
                      <TableHead className=''>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className=''>
                    {mixedWasteFields.map((item, singleMixedWasteFieldIdx) => {
                      return (
                        <TableRow key={item.mixedId} className=''>
                          <TableCell className='py-2'>
                            {singleMixedWasteFieldIdx + 1}
                          </TableCell>
                          <TableCell className='py-2 pl-0'>
                            <FormInput
                              name={`mixedWaste.${singleMixedWasteFieldIdx}.wasteTypeId`}
                              form={form}
                              render={(field) => {
                                return (
                                  <FormSelectSingle
                                    placeholder='Select waste type'
                                    field={field}
                                    className='max-w-[210px] w-full'
                                    options={
                                      wastetypeData?.length
                                        ? wastetypeData?.map((d) => ({
                                            label: d?.name || "",
                                            value: d.id || "",
                                          }))
                                        : []
                                    }
                                    onFieldValueChange={(value) => {
                                      field.onChange(value);
                                      const selectedWasteType =
                                        wastetypeData.find(
                                          (v) => v.id === value,
                                        );
                                      if (selectedWasteType) {
                                        form.setValue(
                                          `mixedWaste.${singleMixedWasteFieldIdx}.wasteName`,
                                          selectedWasteType?.name,
                                        );
                                      }
                                    }}
                                  />
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell className=''>
                            <FormInput
                              name={`mixedWaste.${singleMixedWasteFieldIdx}.percentage`}
                              form={form}
                              input={{
                                type: "number",
                                placeholder: "0",
                                className: "w-16",
                              }}
                            />
                          </TableCell>
                          <TableCell className='py-2'>
                            <Trash2
                              size={20}
                              color='red'
                              onClick={() => {
                                removeWasteFields(singleMixedWasteFieldIdx);
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Button
                  type='button'
                  onClick={() => {
                    appendWasteFields({
                      wasteTypeId: "",
                      percentage: 0,
                    });
                  }}
                >
                  Add new Waste
                </Button>
              </div>
              <div className='my-4'>
                <h2 className='py-4'>Reclaimed & Recycled</h2>
                {/* Reclaimed */}

                <div className='space-y-2'>
                  <Table>
                    <TableHeader>
                      <TableRow className='bg-secondary hover:bg-secondary'>
                        <TableHead className=''>SN</TableHead>
                        <TableHead className='w-full'>WASTE TYPE</TableHead>
                        <TableHead className=''>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {otherWasteFields.map(
                        (item, singleOtherWasteFieldIdx) => {
                          return (
                            <TableRow key={item.otherId}>
                              <TableCell className='py-2'>
                                {singleOtherWasteFieldIdx + 1}
                              </TableCell>
                              <TableCell className='py-2'>
                                <FormInput
                                  name={`otherWaste.${singleOtherWasteFieldIdx}.wasteTypeId`}
                                  form={form}
                                  render={(field) => {
                                    return (
                                      <FormSelectSingle
                                        placeholder='Select waste type'
                                        field={field}
                                        className='max-w-[260px] w-full'
                                        options={
                                          wastetypeData?.length
                                            ? wastetypeData?.map((d) => ({
                                                label: d?.name || "",
                                                value: d.id || "",
                                              }))
                                            : []
                                        }
                                        onFieldValueChange={(value) => {
                                          field.onChange(value);
                                          const selectedWasteType =
                                            wastetypeData.find(
                                              (v) => v.id === value,
                                            );
                                          if (selectedWasteType) {
                                            form.setValue(
                                              `otherWaste.${singleOtherWasteFieldIdx}.wasteName`,
                                              selectedWasteType?.name,
                                            );
                                          }
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </TableCell>
                              <TableCell className='py-2'>
                                <Trash2
                                  size={20}
                                  color='red'
                                  onClick={() =>
                                    removeOtherWasteFields(
                                      singleOtherWasteFieldIdx,
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  </Table>
                  <div>
                    <Button
                      onClick={() => {
                        appendOtherWasteFields({
                          wasteTypeId: "",
                        });
                      }}
                    >
                      Add new Waste
                    </Button>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <SheetFooter className=' !fixed !bottom-0 !w-full !justify-start bg-white p-3 '>
              <Button
                size={"lg"}
                className='w-48 '
                onClick={form.handleSubmit(handleSaveBreakdown)}
              >
                Save Breakdown
              </Button>
              <Button
                variant={"ghost"}
                size={"lg"}
                onClick={() => {
                  form.reset();
                }}
              >
                Reset
              </Button>
            </SheetFooter>
          </div>
        </SheetContent>
      </Sheet>
    </Form>
  );
};

export default AddBreakdownSheet;
